import { useState, useEffect, useCallback, useMemo } from 'react';

import { Center, Spinner, Flex, IconButton, Paragraph, rem } from '@vimeo/bokeh';
import { DownloadImport } from '@vimeo/bokeh-icons';
import type { VideoFile } from '@vimeo/gctl-api/models/components/schemas/video-file/VideoFile';
import { translate } from '@vimeo/i18n-runtime';
import type { WebBpContext } from '@vimeo/use-tracking';
import { useViewer } from '@vimeo/viewer';

import { AudioTrackSelector, VALUE_ORIGINAL_AUDIO_TRACK } from './components/AudioTrackSelector';
import { DrmAlert } from './components/DrmAlert';
import { TryAgainAlert } from './components/TryAgainAlert';
import { WaitProcessing } from './components/WaitProcessing';
import { useClipMetadata } from './hooks/useClipMetadata';
import { useDownloadLinksForVersion } from './hooks/useDownloadLinksForVersion';
import { useGetClipDownloadLinks } from './hooks/useGetClipDownloadLinks';
import { useOnDownloadClick } from './hooks/useOnDownloadClick';
import type { TSelectItem } from './types';
import { filterVideoFilesForKnownResolutionThenSortAlphabetically } from './utils';
import { APP_TYPE, useDataDogGlobals, useErrorTracking, trackingHandle } from './utils/datadog';
import { getVersionId } from './utils/getVersionId';

type Props = {
  files: VideoFile[];
  handleClick?: (file: VideoFile, isExifTagJob?: boolean) => void;
  videoID?: number;
  clipRequestId?: string;
  location?: string;
  className?: string;
  pageName?: WebBpContext['page_name'];
  usesDrm?: boolean;
  audioTracksSelector?: boolean;
  versionUri?: string;
  reviewId?: string;
};

export const DownloadFilesContent = ({
  files,
  handleClick,
  videoID: videoIdFromProps,
  versionUri: versionUriFromProps,
  clipRequestId,
  location,
  className,
  pageName,
  usesDrm: useDrmProps,
  audioTracksSelector: audioTracksSelectorProp = true,
  reviewId,
}: Props) => {
  const [selectedAudioTrackMeta, setSelectedAudioTrackMeta] = useState<TSelectItem>({
    value: VALUE_ORIGINAL_AUDIO_TRACK,
    label: VALUE_ORIGINAL_AUDIO_TRACK,
    isAIGenerated: false,
    isOriginalAudio: true,
  });

  const selectedAudioTrack = selectedAudioTrackMeta.value;

  const videoFilesFromProps = useMemo(() => {
    return filterVideoFilesForKnownResolutionThenSortAlphabetically(files);
  }, [files]);

  const [retryGetLinksHappened, setRetryGetLinksHappened] = useState(false);
  const videoID = clipRequestId ?? (videoIdFromProps ? `${videoIdFromProps}` : undefined);

  const versionIdFromProps = useMemo(() => {
    return versionUriFromProps ? Number(getVersionId(versionUriFromProps)) : undefined;
  }, [versionUriFromProps]);

  const viewer = useViewer();
  const allowAudioTracksSelector = audioTracksSelectorProp && !!viewer?.user && !reviewId;

  const {
    usesDrm,
    mostRecentVersion,
    isFetching: isFetchingMetadata,
    hasVideoTranslations,
    videoLanguage,
    clipMetadataError,
    canEdit,
  } = useClipMetadata({ disabled: !allowAudioTracksSelector, videoId: videoID });

  const versionId = versionIdFromProps ?? mostRecentVersion;
  const versionIsMissing = !isFetchingMetadata && versionId === undefined;

  useDataDogGlobals(
    {
      appType: APP_TYPE,
      userId: viewer?.user?.id,
      userLocale: viewer?.locale,
      modalLocation: location ?? pageName,
      videoId: videoID,
      allowAudioTracksSelector,
      pageName,
    },
    false,
  );

  const defaultAudioTrack = selectedAudioTrack === VALUE_ORIGINAL_AUDIO_TRACK;
  const audiotrackUri = defaultAudioTrack ? undefined : selectedAudioTrack;

  const { onDownloadClick, loadingButtons } = useOnDownloadClick({
    location,
    pageName,
    handleClick,
    videoID: videoIdFromProps,
    audiotrackUri,
    allowAudioTracksSelector,
  });

  const {
    downloadableFiles: mainLinks,
    getVideoFiles,
    isValidating: isVideoFilesValidating,
    isLoading: isVideoFilesLoading,
    videoFilesError,
  } = useGetClipDownloadLinks({
    videoId: videoID,
    disabled: !allowAudioTracksSelector,
  });

  const {
    downloadableFiles: fetchedDownloadLinks,
    downloadsError,
    getDownloads,
    isDownloadsValidating,
    isDownloadsLoading,
  } = useDownloadLinksForVersion({
    versionId,
    videoId: videoID,
    disabled: !allowAudioTracksSelector,
    audiotrackUri,
  });

  useEffect(() => {
    if (audiotrackUri) {
      getDownloads();
    }
  }, [audiotrackUri, getDownloads]);

  const canUseApiFallback = defaultAudioTrack && !videoFilesError && !isVideoFilesLoading;

  const fallbackLinks = useMemo(() => {
    if (!defaultAudioTrack) return null;

    if (canUseApiFallback) return mainLinks;

    return videoFilesFromProps;
  }, [canUseApiFallback, defaultAudioTrack, mainLinks, videoFilesFromProps]);

  const isLoading = isDownloadsLoading || isVideoFilesLoading || isFetchingMetadata;

  const videoFilesObject = useMemo((): {
    files: VideoFile[];
    isFallback?: boolean;
    isLoading?: boolean;
  } => {
    // for 'control' group - always show links from props
    if (!allowAudioTracksSelector) return { files: videoFilesFromProps };

    if (isLoading && fallbackLinks) {
      return { files: fallbackLinks };
    }

    if (isLoading) {
      return {
        isLoading: true,
        files: [],
      };
    }

    // failed to determine Version ID from GET /clip_id or to GET /downloads
    if ((versionIsMissing || downloadsError) && fallbackLinks) {
      return {
        files: fallbackLinks,
        isFallback: true,
      };
    }

    return { files: fetchedDownloadLinks };
  }, [
    isLoading,
    allowAudioTracksSelector,
    videoFilesFromProps,
    versionIsMissing,
    downloadsError,
    fallbackLinks,
    fetchedDownloadLinks,
  ]);

  const videoFiles = videoFilesObject.files;

  const isFallbackLinksUsed = videoFilesObject.isFallback;
  const isNoLinksToShow = videoFiles.length === 0 && !videoFilesObject.isLoading;

  useErrorTracking({
    versionIsMissing,
    clipMetadataError,
    videoFilesError,
    downloadsError,
    videoID,
    versionId,
    selectedAudioTrack,
    isFallbackLinksUsed,
    isNoLinksToShow,
    allowAudioTracksSelector,
  });

  const backToOriginalLinks = useCallback(() => {
    setSelectedAudioTrackMeta({
      value: VALUE_ORIGINAL_AUDIO_TRACK,
      label: VALUE_ORIGINAL_AUDIO_TRACK,
      isAIGenerated: false,
      isOriginalAudio: true,
    });
  }, []);

  const retryGetLinks = useCallback(() => {
    getVideoFiles();
    getDownloads();
  }, [getDownloads, getVideoFiles]);

  const onRetry = useCallback(() => {
    if (retryGetLinksHappened && audiotrackUri) {
      backToOriginalLinks();
    } else {
      retryGetLinks();
      setRetryGetLinksHappened(true);
    }
    trackingHandle.downloadLinks.userRetriesGetLinks({
      audiotrackUri,
    });
  }, [audiotrackUri, backToOriginalLinks, retryGetLinks, retryGetLinksHappened]);

  const onSelectAudioTrack = useCallback((selection: TSelectItem) => {
    trackingHandle.audioTrackSelector.userSelectedAudioTrack(selection);
    setSelectedAudioTrackMeta(selection);
  }, []);

  const showRetryNotice = allowAudioTracksSelector && isNoLinksToShow && downloadsError;

  useEffect(() => {
    if (showRetryNotice) {
      trackingHandle.downloadLinks.onLinksRetryRendered();
    }
  }, [showRetryNotice]);

  return (
    <>
      {(usesDrm || useDrmProps) && <DrmAlert />}
      <Flex
        gap="1.75rem"
        direction="column"
        className={className}
        minHeight={allowAudioTracksSelector ? rem(300) : undefined}
      >
        {allowAudioTracksSelector && (
          <AudioTrackSelector
            videoID={videoID}
            setSelectedAudioTrack={onSelectAudioTrack}
            selectedAudioTrack={selectedAudioTrack}
            versionId={versionId}
            videoLanguage={videoLanguage}
            hasVideoTranslations={hasVideoTranslations}
            hasEditRights={canEdit}
          />
        )}
        {showRetryNotice && (
          <TryAgainAlert
            isLoading={isDownloadsValidating || isVideoFilesValidating}
            onRetry={onRetry}
          />
        )}
        {videoFilesObject.isLoading && (
          <Center w="100%" h="100%" margin="auto">
            <Spinner size="md" />
          </Center>
        )}
        {isNoLinksToShow && !downloadsError ? (
          <WaitProcessing />
        ) : (
          videoFiles.map((file) => {
            return (
              <Flex
                align="center"
                justify="space-between"
                key={`${selectedAudioTrack}-${file.link}`}
                id={`download-file-${file.link}`}
              >
                <Flex gap="0.75rem" align="center">
                  <Paragraph size="lg" fontWeight="500" minW="3.13rem">
                    {file.quality === 'source' ? translate('Original') : file.publicName}
                  </Paragraph>
                  <Flex gap="xs">
                    <Paragraph size="sm" color="text-secondary">
                      {file.sizeShort}
                    </Paragraph>
                    <Paragraph size="sm" color="text-secondary">
                      |
                    </Paragraph>
                    <Paragraph size="sm" color="text-secondary">
                      {file.width} x {file.height}
                    </Paragraph>
                  </Flex>
                </Flex>
                <IconButton
                  aria-label={translate('Download')}
                  as="a"
                  href={`${file.link}`}
                  variant="secondary"
                  icon={<DownloadImport />}
                  isDisabled={videoFilesObject.isLoading}
                  isLoading={loadingButtons.indexOf(file.link) !== -1}
                  onClick={onDownloadClick(file, selectedAudioTrackMeta)}
                />
              </Flex>
            );
          })
        )}
      </Flex>
    </>
  );
};
